/**
 * Changes Visualizer Panel
 * Affiche les changements proposés et effectués de manière visuelle
 */

import React, { useState } from 'react';

export interface Change {
  sheet: string;
  cell: string;
  before: string | number;
  after: string | number;
  type: 'formula' | 'value' | 'format';
  status: 'proposed' | 'applied' | 'failed';
}

export interface ChangesVisualizerProps {
  changes: Change[];
  title?: string;
  onApplyAll?: () => void;
  onRejectAll?: () => void;
}

/**
 * Composant pour visualiser les changements
 */
export const ChangesVisualizerPanel: React.FC<ChangesVisualizerProps> = ({
  changes,
  title = 'Changements Proposés',
  onApplyAll,
  onRejectAll,
}) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | 'proposed' | 'applied' | 'failed'>('all');

  // Filtrer les changements
  const filteredChanges = changes.filter((change) => {
    if (filterStatus === 'all') return true;
    return change.status === filterStatus;
  });

  // Compter les changements par statut
  const stats = {
    total: changes.length,
    proposed: changes.filter((c) => c.status === 'proposed').length,
    applied: changes.filter((c) => c.status === 'applied').length,
    failed: changes.filter((c) => c.status === 'failed').length,
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'proposed':
        return '#FFC107'; // Amber
      case 'applied':
        return '#4CAF50'; // Green
      case 'failed':
        return '#F44336'; // Red
      default:
        return '#999';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'proposed':
        return '⏳ Proposé';
      case 'applied':
        return '✅ Appliqué';
      case 'failed':
        return '❌ Échoué';
      default:
        return status;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'formula':
        return '📐';
      case 'value':
        return '📊';
      case 'format':
        return '🎨';
      default:
        return '📝';
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* En-tête */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>{title}</h3>
        <div style={{ display: 'flex', gap: '8px', fontSize: '12px' }}>
          <span style={{ padding: '4px 8px', backgroundColor: '#FFC107', borderRadius: '4px', color: 'white' }}>
            {stats.proposed} proposés
          </span>
          <span style={{ padding: '4px 8px', backgroundColor: '#4CAF50', borderRadius: '4px', color: 'white' }}>
            {stats.applied} appliqués
          </span>
          {stats.failed > 0 && (
            <span style={{ padding: '4px 8px', backgroundColor: '#F44336', borderRadius: '4px', color: 'white' }}>
              {stats.failed} échoués
            </span>
          )}
        </div>
      </div>

      {/* Filtres */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {['all', 'proposed', 'applied', 'failed'].map((status) => (
          <button
            key={status}
            onClick={() => setFilterStatus(status as any)}
            style={{
              padding: '6px 12px',
              backgroundColor: filterStatus === status ? getStatusColor(status) : '#f0f0f0',
              color: filterStatus === status ? 'white' : '#333',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: filterStatus === status ? 'bold' : 'normal',
              transition: 'all 0.2s',
            }}
          >
            {status === 'all' ? 'Tous' : getStatusLabel(status)}
          </button>
        ))}
      </div>

      {/* Boutons d'action */}
      {stats.proposed > 0 && (
        <div style={{ display: 'flex', gap: '8px' }}>
          {onApplyAll && (
            <button
              onClick={onApplyAll}
              style={{
                padding: '8px 16px',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 'bold',
              }}
            >
              ✅ Appliquer tous les changements
            </button>
          )}
          {onRejectAll && (
            <button
              onClick={onRejectAll}
              style={{
                padding: '8px 16px',
                backgroundColor: '#F44336',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 'bold',
              }}
            >
              ❌ Rejeter tous les changements
            </button>
          )}
        </div>
      )}

      {/* Liste des changements */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {filteredChanges.length === 0 ? (
          <div
            style={{
              padding: '16px',
              backgroundColor: '#f5f5f5',
              borderRadius: '4px',
              textAlign: 'center',
              color: '#999',
              fontSize: '13px',
            }}
          >
            Aucun changement à afficher
          </div>
        ) : (
          filteredChanges.map((change, index) => (
            <div
              key={index}
              style={{
                border: `2px solid ${getStatusColor(change.status)}`,
                borderRadius: '4px',
                overflow: 'hidden',
                backgroundColor: '#fafafa',
              }}
            >
              {/* En-tête du changement */}
              <div
                onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                style={{
                  padding: '12px',
                  backgroundColor: getStatusColor(change.status),
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  userSelect: 'none',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>{getTypeIcon(change.type)}</span>
                  <span style={{ fontWeight: 'bold' }}>
                    {change.sheet}!{change.cell}
                  </span>
                  <span style={{ fontSize: '12px', opacity: 0.9 }}>{getStatusLabel(change.status)}</span>
                </div>
                <span style={{ fontSize: '16px' }}>{expandedIndex === index ? '▼' : '▶'}</span>
              </div>

              {/* Détails du changement */}
              {expandedIndex === index && (
                <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {/* Avant */}
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#666', marginBottom: '4px' }}>
                      📥 Avant:
                    </div>
                    <div
                      style={{
                        padding: '8px',
                        backgroundColor: '#fff3cd',
                        borderRadius: '4px',
                        fontFamily: 'monospace',
                        fontSize: '12px',
                        wordBreak: 'break-all',
                        color: '#856404',
                      }}
                    >
                      {String(change.before)}
                    </div>
                  </div>

                  {/* Flèche */}
                  <div style={{ textAlign: 'center', color: '#999', fontSize: '14px' }}>↓</div>

                  {/* Après */}
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#666', marginBottom: '4px' }}>
                      📤 Après:
                    </div>
                    <div
                      style={{
                        padding: '8px',
                        backgroundColor: '#d4edda',
                        borderRadius: '4px',
                        fontFamily: 'monospace',
                        fontSize: '12px',
                        wordBreak: 'break-all',
                        color: '#155724',
                      }}
                    >
                      {String(change.after)}
                    </div>
                  </div>

                  {/* Métadonnées */}
                  <div style={{ fontSize: '11px', color: '#999', marginTop: '8px' }}>
                    <div>Type: {change.type}</div>
                    <div>Statut: {getStatusLabel(change.status)}</div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Résumé */}
      {changes.length > 0 && (
        <div
          style={{
            padding: '12px',
            backgroundColor: '#e3f2fd',
            borderRadius: '4px',
            fontSize: '12px',
            color: '#1565c0',
            borderLeft: '4px solid #1976d2',
          }}
        >
          <strong>Résumé:</strong> {stats.total} changement(s) total(aux) - {stats.applied} appliqué(s),{' '}
          {stats.proposed} proposé(s), {stats.failed} échoué(s)
        </div>
      )}
    </div>
  );
};
